import { sizes } from '@sharefiledev/antd-config';
import {
	ClientDashboardDrawerCommentsTabExtensionParams,
	clientDashboardExtensionSlots,
} from '@sharefiledev/client-dashboard';
import {
	conversationsExtensionSlots,
	ConversationsTileParams,
} from '@sharefiledev/conversations-pilet-sdk';
import { Tab, WorkflowsIcon } from '@sharefiledev/flow-web';
import { requestListExtensionSlots } from '@sharefiledev/request-list-pilet-sdk';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import type { PiletApi } from '@sharefiledev/sharefile-appshell';
import { QueryClient } from '@tanstack/react-query';
import { addBlocks } from './Blocks';
import { configureConversationHttp } from './ConversationHttp';
import {
	AsyncLazyClientDashboardTabExtension,
	AsyncLazyCommentsTabExtension,
	AsyncLazyConversationsTileExtension,
	AsyncLazyConversationsWrapper,
	AsyncLazyConversationTabWrapper,
	AsyncLazyDemoPage,
	AsyncLazyItemRowMetadata,
} from './Extensions';
import { setLogger } from './logger';

const conversationTabWrapperStyle = {
	display: 'flex',
	paddingTop: '15px',
	margin: 'auto',
	maxWidth: '700px',
	maxHeight: 'calc(100% - 15px)',
};

export function setup(app: PiletApi) {
	setLogger(app.sf.getLogger());
	const queryClient = new QueryClient();

	addBlocks(app, queryClient);

	app.registerExtension(requestListExtensionSlots.itemDetailsTabs, ({ params }) => {
		return (
			<AsyncLazyConversationTabWrapper
				app={app}
				queryClient={queryClient}
				tabContext={params.tabContext}
				resourceId={params.resourceId}
				containerName={params.containerName}
				actorIds={params.actorIds}
				resourceTitle={params.resourceTitle}
				pendoTrackingNamespace={params.pendoTrackingNamespace}
				originAnalyticsTrackingId={params.originAnalyticsTrackingId}
				enableFilters={false}
				style={{
					display: 'flex',
					justifyContent: 'center',
					margin: 'auto',
					marginLeft: '1px',
					padding: `${sizes.base} ${sizes.SM}`,
				}}
			/>
		);
	});

	app.registerExtension(requestListExtensionSlots.itemRowMetadata, ({ params }) => {
		return <AsyncLazyItemRowMetadata app={app} client={queryClient} params={params} />;
	});

	app.registerExtension(
		'urn:sfextslot:projects-pilet:tabs:conversations',
		({ params }) => {
			return (
				<AsyncLazyConversationTabWrapper
					app={app}
					queryClient={queryClient}
					tabContext={params.tabContext}
					resourceId={params.projectUri}
					pendoTrackingNamespace={params.pendoTrackingNamespace}
					enableFilters
					style={conversationTabWrapperStyle}
					isFrozen={!!params.isFrozen}
					resourceTitle={params.resourceTitle}
					containerName={params.containerName}
					notifications={params.notifications}
					allowedMentionRoles={params.allowedMentionRoles}
				/>
			);
		}
	);

	app.registerExtension(conversationsExtensionSlots.projectTab, ({ params }) => {
		return (
			<AsyncLazyConversationTabWrapper
				app={app}
				actorIds={params.actorIds}
				queryClient={queryClient}
				tabContext={params.tabContext}
				resourceId={params.projectUri}
				pendoTrackingNamespace={params.pendoTrackingNamespace}
				enableFilters={params.enableFilters ?? true}
				style={conversationTabWrapperStyle}
				isFrozen={!!params.isFrozen}
				originAnalyticsTrackingId={params.originAnalyticsTrackingId}
				resourceTitle={params.resourceTitle}
				containerName={params.containerName}
				notifications={params.notifications}
				allowedMentionRoles={params.allowedMentionRoles}
			/>
		);
	});

	app.registerExtension<ConversationsTileParams>(
		conversationsExtensionSlots.tile,
		({ params }) => {
			return (
				<AsyncLazyConversationsTileExtension
					app={app}
					queryClient={queryClient}
					params={params}
				/>
			);
		}
	);

	app.registerExtension<ClientDashboardDrawerCommentsTabExtensionParams>(
		clientDashboardExtensionSlots.drawerCommentsTab,
		({ params }) => {
			return (
				<AsyncLazyClientDashboardTabExtension
					app={app}
					queryClient={queryClient}
					params={params}
				/>
			);
		}
	);

	app.registerExtension(clientDashboardExtensionSlots.commentsTab, ({ params }) => {
		return (
			<AsyncLazyCommentsTabExtension
				app={app}
				queryClient={queryClient}
				params={params}
			/>
		);
	});

	app.registerExtension(
		clientDashboardExtensionSlots.drawerCommentsContent,
		({ params }) => {
			return (
				<AsyncLazyConversationsWrapper
					app={app}
					queryClient={queryClient}
					resourceId={params.resourceUri}
					actorIds={params.actorIds}
					resourceTitle={params.resourceTitle}
					containerName={params.containerName}
					enableFilters={false}
					pendoTrackingNamespace={params.pendoTrackingNamespace} // This prop is added to enable Pendo tracking functionality specifically for the client dashboard.
					isClientSide
					isFrozen={!!params.isFrozen}
					notifications={params.notifications}
					allowedMentionRoles={params.allowedMentionRoles}
				/>
			);
		}
	);

	app.registerExtension('sf-conversations-empty', () => {
		return <h1>Select a task to view its conversations.</h1>;
	});

	app.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'conversation_msg',
		defaultPath: 'projects/{projectId}/Comments',
		clientPath: '/client?taskRID={resourceId}',
		callback: async (rid: string) => {
			return {
				projectId: RID.parse(rid).findEntityType('project').entityId,
				resourceId: RID.parse(rid).parent().parent(),
			};
		},
	});

	app.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'conversation',
		defaultPath: '{resourcePath}conversation={conversationId}',
		clientPath: '{resourcePath}conversation={conversationId}',
		callback: async (rid: string) => {
			const conversationId = RID.parse(rid).entityId;
			const parentRID = RID.parse(rid).parent();
			const parentPath = await app.sf.getViewEngineResourceURLRewritePath(
				parentRID.toString()
			);
			const separator = parentPath.includes('?') ? '&' : '?';
			return {
				resourceId: parentRID,
				resourcePath: parentPath + separator,
				conversationId,
			};
		},
	});
	if (process.env.NODE_ENV === 'development') {
		if (typeof app.meta.config.apiOrigin === 'string') {
			configureConversationHttp(config => {
				config.origin = app.meta.config.apiOrigin + '/io/conversations';
			});
		}

		app.sf.registerLeftNavComponent({
			href: '/conversations',
			title: () => 'Conversations',
			icon: () => <WorkflowsIcon />,
			weight: 50,
		});

		app.registerExtension('devTab', ({ params }) => {
			return (
				<AsyncLazyConversationTabWrapper
					app={app}
					queryClient={queryClient}
					tabContext={params.tabContext}
					resourceId={params.resourceId}
					pendoTrackingNamespace={params.pendoTrackingNamespace}
					enableFilters={false}
					style={{
						padding: '10px',
						display: 'flex',
						flexDirection: 'column',
						margin: 'auto',
						maxWidth: '500px',
						maxHeight: '700px',
					}}
					isFrozen={!!params.isFrozen}
				/>
			);
		});

		app.registerExtension('clickme-tab', () => {
			const clickMe = 'Click Me!';
			return <Tab value={clickMe} label={clickMe} />;
		});

		app.registerPage('/conversations', () => <AsyncLazyDemoPage piletApi={app} />);
	}
}
